import { RegistryPrompt } from './RegistryPrompt'
import { useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { SAMPLE_PROFILES } from '../data/sampleProfiles'
import { PRODUCT_NAME, pageFor } from '../pages'
import { RESOURCES } from '../resources'
import type { CatalogState } from '../state/catalogState'
import type { ReportsState } from '../state/reports'
import { Button } from '../ui/Button'
import { Icon } from '../ui/Icon'
import { Suggestion } from '../ui/Suggestion'
import { Notice } from '../ui/primitives'

export function Welcome({
  state,
  catalog,
  guide,
}: {
  state: ReportsState
  catalog: CatalogState
  /** The getting-started guide: how far along it is, and how to open it. */
  guide?: { remaining: number; total: number; open: () => void }
}) {
  const guideDone = guide ? guide.total - guide.remaining : 0
  const [dragging, setDragging] = useState(false)
  const [showSamples, setShowSamples] = useState(false)
  const input = useRef<HTMLInputElement>(null)

  // Sent here from a page that needs assessments, with nothing loaded yet.
  const from = (useLocation().state as { from?: string } | null)?.from
  const opened = from ? pageFor(from.split('?')[0]!) : undefined
  const linkedPage = opened

  return (
    <div className="welcome-container rise">
      {linkedPage && (
        <Notice tone="warn">
          You opened <strong>{linkedPage.label}</strong>, which needs assessments loaded in this
          browser. Load some below, from files or the samples, and it opens.
        </Notice>
      )}
      <header className="welcome-hero">
        <div className="welcome-title-row">
          <span className="welcome-hero-icon" aria-hidden="true">
            <Icon name="home" size={20} />
          </span>
          <h1 className="welcome-title">Welcome</h1>
        </div>
        <p className="welcome-subtitle">
          Reads resqui assessment reports and shows the results for each piece of software, side by
          side and over time.
        </p>
      </header>

      {/* Follows the guide: an offer before it is begun, the progress once it is, nothing when done. */}
      {guide && guide.remaining > 0 && (
        <Suggestion
          label="Getting started"
          icon="listChecks"
          title={
            guideDone === 0
              ? `New to ${PRODUCT_NAME}?`
              : `Getting started: ${guideDone} of ${guide.total} done`
          }
          actions={
            <Button
              tone="primary"
              size="small"
              icon="listChecks"
              label={guideDone === 0 ? 'Getting started' : 'Continue'}
              onClick={() => guide.open()}
            />
          }
        >
          {guideDone === 0 ? (
            <p className="small muted m-0">
              A few short steps, from the samples to assessing and following your own software.
            </p>
          ) : (
            <>
              <span className="welcome-guide-progress" aria-hidden="true">
                <span style={{ width: `${(guideDone / guide.total) * 100}%` }} />
              </span>
              <p className="small muted m-0">Pick up where you left off.</p>
            </>
          )}
        </Suggestion>
      )}

      <section className="welcome-card" aria-labelledby="upload-head">
        <div className="welcome-card-header">
          <div className="icon-badge">
            <Icon name="upload" size={20} />
          </div>
          <h2 id="upload-head" className="card-title m-0">
            Load assessment reports
          </h2>
        </div>

        <div
          className="modern-dropzone"
          data-active={dragging}
          onDragOver={(event) => {
            event.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault()
            setDragging(false)
            void state.addFiles(Array.from(event.dataTransfer.files))
          }}
        >
          <div className="dropzone-header-row">
            <div className="dropzone-icon-ring">
              <Icon name="upload" size={20} />
            </div>
            <strong className="dropzone-title">Drop resqui JSON reports here</strong>
          </div>
          <Button
            tone="primary"
            icon="plus"
            label="Choose files"
            disabled={state.busy}
            onClick={() => input.current?.click()}
          />
          <input
            ref={input}
            type="file"
            accept="application/json,.json"
            multiple
            aria-label="Choose assessment report files"
            className="visually-hidden"
            onChange={(event) => {
              if (event.target.files) void state.addFiles(Array.from(event.target.files))
              event.target.value = ''
            }}
          />
        </div>
      </section>

      <button
        type="button"
        className="welcome-reveal"
        aria-expanded={showSamples}
        aria-controls="sample-panel"
        data-tour="explore-samples"
        onClick={() => setShowSamples(!showSamples)}
      >
        <span className="icon-badge" aria-hidden="true">
          <Icon name="chart" size={20} />
        </span>
        <span className="welcome-reveal-text">
          <strong className="card-title">Explore sample projects</strong>
          <span className="card-desc">
            Real resqui assessments of open research software, EVERSE projects among them.
          </span>
        </span>
        <Icon name={showSamples ? 'chevronUp' : 'chevronDown'} size={16} />
      </button>

      {showSamples && (
        <section id="sample-panel" className="welcome-card" aria-label="Sample projects">
          <div className="sample-action-cta">
            <Button
              tone="primary"
              icon="download"
              label="Load all sample assessments"
              data-tour="load-all-samples"
              disabled={state.busy}
              onClick={() => void state.addSamples()}
            />
          </div>

          <div className="sample-picker-section">
            <span className="section-micro-label">
              Or load repositories assessed against one resqui profile:
            </span>
            <div className="sample-profiles-showcase">
              {SAMPLE_PROFILES.map((sample) => (
                <div key={sample.id} className="sample-profile-card">
                  <div>
                    <div className="sample-profile-head">
                      <div className="sample-profile-icon" aria-hidden="true">
                        <Icon name={sample.icon} size={16} />
                      </div>
                      <span className="chip tag-accent">{sample.tag}</span>
                    </div>
                    <strong
                      className="mt-2"
                      style={{
                        display: 'block',
                        fontSize: 'var(--fs-base)',
                        color: 'var(--text-strong)',
                      }}
                    >
                      {sample.name}
                    </strong>
                    <div className="sample-profile-tags">
                      <span className="sample-profile-tag">
                        {sample.files.length}{' '}
                        {sample.files.length === 1 ? 'repository' : 'repositories'}
                      </span>
                    </div>
                    <p className="small muted m-0 mt-1">{sample.desc}</p>
                  </div>
                  <Button
                    tone="primary"
                    size="small"
                    icon="download"
                    label="Quick load"
                    disabled={state.busy}
                    onClick={() =>
                      void state.addFromUrls(
                        sample.files.map((file) => `${import.meta.env.BASE_URL}samples/${file}`),
                      )
                    }
                    title={`Load the ${sample.name} profile samples`}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <RegistryPrompt state={state} catalog={catalog} />

      <section className="welcome-resources" aria-labelledby="resources-head">
        <h2 id="resources-head" className="section-micro-label">
          Learn more
        </h2>
        <ul className="resource-list">
          {RESOURCES.map((resource) => (
            <li key={resource.url}>
              <a href={resource.url} target="_blank" rel="noreferrer">
                {resource.name}
              </a>
              <span className="small muted">{resource.what}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}
